import { useState } from "react"
import {
  CheckCircleOutlined,
  DeleteOutlined,
  RollbackOutlined,
} from "@ant-design/icons"
import { Button, Checkbox, Flex, Modal, Steps, Typography } from "antd"
import { Alert } from "~/components/Alert"
import styles from "./index.css"

const { Text } = Typography

export type CleanupAction = "refund" | "confirm" | "delete" | null

export const WorkflowStatus = ({
  detail,
}: {
  detail: API.Staff.OrderCleanupDetailResponse
}) => {
  let step = 0
  let status: "error" | "process" = "process"
  let alertType: "error" | "warning" = "warning"
  let title = "订单待核对"
  let description = "请先核对订单和关联记录。"

  if (!detail.cleanup_eligible) {
    alertType = "error"
    title = detail.blocked_reason || "该订单不可退款或清理"
    description = "订单详情仅供核对，当前不能执行高危操作。"
  } else if (detail.refund?.status === "FAILED") {
    step = 1
    status = "error"
    alertType = "error"
    title = "上一次退款申请失败"
    description = "请核对失败原因，确认问题已处理后重新发起退款。"
  } else if (detail.available_actions.can_refund) {
    step = 1
    title = "订单符合清理条件，可以发起退款"
    description = "退款申请不会删除数据，到账后还需要人工确认。"
  } else if (detail.available_actions.can_confirm_refund) {
    step = 2
    title = "退款申请已受理，等待人工确认到账"
    description = "请通过微信退款记录或用户反馈核实实际到账，再执行确认。"
  } else if (detail.available_actions.can_delete && detail.can_delete) {
    step = 3
    alertType = "error"
    title = "退款已人工确认到账，可以删除订单"
    description = "删除操作不可恢复，请最后核对全部关联记录。"
  } else {
    step = 2
    alertType = "warning"
    title = "退款操作处理中"
    description = "当前没有可执行操作，请稍后重新查询订单状态。"
  }

  return (
    <>
      <Steps
        className={styles.workflowSteps}
        current={step}
        items={[
          { title: "核对订单" },
          { title: "发起退款" },
          { title: "确认到账" },
          { title: "删除订单" },
        ]}
        status={status}
      />
      <Alert
        className={styles.eligibilityAlert}
        description={description}
        showIcon
        title={title}
        type={alertType}
      />
    </>
  )
}

export const CleanupActions = ({
  detail,
  activeAction,
  onRequestRefund,
  onConfirmRefund,
  onDelete,
}: {
  detail: API.Staff.OrderCleanupDetailResponse
  activeAction: CleanupAction
  onRequestRefund: () => void
  onConfirmRefund: () => Promise<void>
  onDelete: () => void
}) => {
  const [confirmRefundOpen, setConfirmRefundOpen] = useState(false)
  const [refundReceived, setRefundReceived] = useState(false)
  const actions = detail.available_actions
  const canDelete = actions.can_delete && detail.can_delete
  const hasAction =
    actions.can_refund || actions.can_confirm_refund || canDelete
  const actionLabel =
    detail.cleanup_type === "wallet" ? "删除积点订单" : "删除专业版订单"

  if (!hasAction) return null

  const closeConfirmModal = () => {
    setConfirmRefundOpen(false)
    setRefundReceived(false)
  }

  const submitConfirmation = async () => {
    if (!refundReceived) return

    try {
      await onConfirmRefund()
      closeConfirmModal()
    } catch {
      // The request layer shows the error; keep this confirmation open.
    }
  }

  return (
    <>
      <Flex
        className={styles.actionBar}
        gap={12}
        justify="flex-end"
        wrap
      >
        {actions.can_refund ? (
          <Button
            danger
            icon={<RollbackOutlined />}
            loading={activeAction === "refund"}
            size="large"
            type="primary"
            onClick={onRequestRefund}
          >
            发起微信退款
          </Button>
        ) : null}
        {actions.can_confirm_refund ? (
          <Button
            danger
            icon={<CheckCircleOutlined />}
            loading={activeAction === "confirm"}
            size="large"
            onClick={() => setConfirmRefundOpen(true)}
          >
            确认退款已到账
          </Button>
        ) : null}
        {canDelete && detail.cleanup_type ? (
          <Button
            danger
            icon={<DeleteOutlined />}
            loading={activeAction === "delete"}
            size="large"
            type="primary"
            onClick={onDelete}
          >
            {actionLabel}
          </Button>
        ) : null}
      </Flex>

      <Modal
        cancelText="继续核实"
        confirmLoading={activeAction === "confirm"}
        destroyOnHidden
        okButtonProps={{ danger: true, disabled: !refundReceived }}
        okText="确认退款已到账"
        open={confirmRefundOpen}
        title="人工确认退款到账"
        width={560}
        onCancel={() => {
          if (activeAction !== "confirm") closeConfirmModal()
        }}
        onOk={() => void submitConfirmation()}
      >
        <Flex
          className={styles.confirmContent}
          gap={16}
          vertical
        >
          <Alert
            description="系统无法自动判断用户是否实际收到退款。确认后订单将进入可删除状态，并记录当前 Staff 的确认时间和身份。"
            showIcon
            title="这是人工财务确认，不是查询退款进度"
            level="strong"
          />
          <Text>订单号：{detail.order.order_no}</Text>
          <Checkbox
            checked={refundReceived}
            disabled={activeAction === "confirm"}
            onChange={(event) => setRefundReceived(event.target.checked)}
          >
            我已通过可靠渠道核实，该笔退款已经实际到账
          </Checkbox>
        </Flex>
      </Modal>
    </>
  )
}
